import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Button, Form, FormGroup, Label, Input, Spinner, Row, Col, Alert } from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { setBreadcrumbItems } from '../store/actions';
import api from '../utils/api';

const UserEdit = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    props.setBreadcrumbItems('تعديل مستخدم', [
      { title: 'الرئيسية', link: '/dashboard' },
      { title: 'المستخدمين', link: '/dashboard/users' },
      { title: 'تعديل مستخدم', link: '#' }
    ]);
    loadCurrentUser();
    fetchUser();
    // eslint-disable-next-line
  }, [id]);

  const loadCurrentUser = () => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) setCurrentUser(JSON.parse(raw));
    } catch { }
    finally { setCheckingAuth(false); }
  };

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/users/${id}`);
      setUser(res.data?.user || res.data);
    } catch {
      setError('تعذر جلب بيانات المستخدم');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUser(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const dataToSend = { ...user };
      if (!dataToSend.password) delete dataToSend.password;
      await api.put(`/users/${id}`, dataToSend);
      setSuccess('تم حفظ التعديلات بنجاح');
      setTimeout(() => navigate('/dashboard/users'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';

  if (checkingAuth || loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 mb-0">جار التحميل...</p>
      </div>
    );
  }

  if (error && !user) {
    return (
      <Card>
        <CardBody>
          <Alert color="danger">{error}</Alert>
          <Button color="primary" onClick={() => navigate('/dashboard/users')}>العودة للقائمة</Button>
        </CardBody>
      </Card>
    );
  }

  if (!user || !isAdmin) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <i className="fas fa-lock fa-3x mb-3 text-muted"></i>
          <h5>غير مسموح</h5>
          <p className="text-muted">ليس لديك صلاحية لتعديل هذا المستخدم</p>
          <Button color="primary" onClick={() => navigate('/dashboard')}>العودة للرئيسية</Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <CardTitle className="h4 mb-4">تعديل المستخدم: {user.name}</CardTitle>
        {error && <Alert color="danger">{error}</Alert>}
        {success && <Alert color="success">{success}</Alert>}
        <Form onSubmit={handleSave}>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>الاسم</Label>
                <Input name="name" value={user.name || ''} onChange={handleChange} required className="form-control-lg" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>البريد الإلكتروني</Label>
                <Input name="email" type="email" value={user.email || ''} onChange={handleChange} required className="form-control-lg" />
              </FormGroup>
            </Col>
          </Row>

          {isAdmin && user.id !== 1 && (
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>الصلاحية</Label>
                  <Input type="select" name="role" value={user.role || 'user'} onChange={handleChange} className="form-control-lg">
                    <option value="user">مستخدم عادي</option>
                    <option value="data_entry">مدخل بيانات</option>
                    <option value="admin">أدمن</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mt-4">
                  <div className="form-check mt-2">
                    <Input
                      type="checkbox"
                      name="is_active"
                      id="is_active"
                      checked={!!user.is_active}
                      onChange={handleChange}
                      className="form-check-input"
                    />
                    <Label for="is_active" className="form-check-label ms-2">مفعل</Label>
                  </div>
                </FormGroup>
              </Col>
            </Row>
          )}

          <FormGroup>
            <Label>كلمة المرور الجديدة (اختياري)</Label>
            <Input
              name="password"
              type="password"
              value={user.password || ''}
              onChange={handleChange}
              placeholder="اتركه فارغاً إن لم ترغب بالتغيير"
              className="form-control-lg"
            />
            <small className="text-muted">لن تتغير كلمة المرور إن تركته فارغاً</small>
          </FormGroup>

          <div className="mt-4">
            <Button color="primary" type="submit" disabled={saving} className="me-2">
              {saving ? (<><Spinner size="sm" className="me-1" /> جاري الحفظ...</>) : 'حفظ'}
            </Button>
            <Button color="secondary" type="button" onClick={() => navigate('/dashboard/users')}>إلغاء</Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

export default connect(null, { setBreadcrumbItems })(UserEdit);
